import { CommonModule, Location } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { MultiSelectModule } from 'primeng/multiselect';
import { DisputeService } from 'src/app/core/services/dispute.service';
import {
  DisputeData,
  DisputeFilter,
  DisputeFilterParams,
  DisputeResponse,
  DisputeStatusResponse,
  DisputeTypeResponse,
} from 'src/app/shared/model/dispute.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { Router } from '@angular/router';
import { TABLE_PARAM, STATUSES } from 'src/app/shared/constants/common.constants';
import { domainUrl } from 'src/environments/environment';
import { CommonService } from 'src/app/shared/services/common.service';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { PaginationService } from 'src/app/shared/providers/pagination/pagination.service';

@Component({
  selector: 'app-disputes',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    TableModule,
    TranslateModule,
    ToastModule,
    ButtonModule,
    DropdownModule,
    FormsModule,
    MultiSelectModule,
  ],
  providers: [DisputeService, TranslateService],
  templateUrl: './disputes.component.html',
  styleUrl: './disputes.component.scss',
})
export class DisputesComponent implements OnInit {
  statuses = STATUSES;

  tableParams = { ...TABLE_PARAM };

  noDataRow = [{}];

  first = 1;

  rows = TABLE_PARAM.PAGE_SIZE;

  sortField = 'CreatedOn';

  sortOrder = TABLE_PARAM.SORT_DEC_ORDER;

  disputeData: DisputeResponse['data'] | null = null;

  disputeItems: DisputeData['items'] | [] = [];

  disputePageNumber: DisputeData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  disputePageSize: DisputeData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  totalRecords = 0;

  baseApiUrl = domainUrl.url;

  filePath: DisputeResponse['filePath'];

  disputeTypes: DisputeTypeResponse['data'] | [] = [];

  disputeStatuses: DisputeStatusResponse['data'] | [] = [];

  filters: DisputeFilter = {
    userName: '',
    disputeStatus: [],
    disputeTypes: [],
  };

  constructor(
    private disputeService: DisputeService,
    private toastService: ToasterService,
    private translateService: TranslateService,
    private location: Location,
    private router: Router,
    private paginationService: PaginationService,
  ) {}

  ngOnInit() {
    this.tableParams.ROW_PER_PAGE = this.paginationService.getAllowedSizes();
    this.rows = this.paginationService.getPageSize('dispute');
    this.disputePageSize = this.rows;
    this.disputePageNumber = 1;
    this.getDisputeTypes();
    this.getDisputeStatuses();
    this.getDisputeData(
      this.disputePageNumber,
      this.disputePageSize,
      this.sortField,
      this.sortOrder,
      false,
    );
  }

  exportData() {
    this.getDisputeData(
      this.disputePageNumber,
      this.disputePageSize,
      this.sortField,
      this.sortOrder,
      true,
    );
    this.toastService.showSuccessToast(
      this.translateService.instant('DISPUTE_PAGE.TOAST_MESSAGE.SUCCESS_TO_EXPORT_DISPUTE_DATA'),
    );
  }

  onResetFilters() {
    this.filters = {
      userName: '',
      disputeTypes: [],
      disputeStatus: [],
    };
    this.disputePageNumber = 1;
    this.applyFilters();
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  applyFilters(): void {
    this.first = 0;
    this.disputePageNumber = this.disputePageNumber === 0 ? 1 : this.disputePageNumber;
    this.getDisputeData(
      this.disputePageNumber,
      this.disputePageSize,
      this.sortField,
      this.sortOrder,
      false,
    );
  }

  pageChange(event: { first: number; rows: number }) {
    this.first = event.first;
    this.rows = event.rows;
    this.paginationService.setPageSize('dispute', this.rows);
    this.disputePageSize = this.rows;
    this.disputePageNumber = this.first / this.rows;
    this.disputePageSize = this.rows;
    this.getDisputeData(
      this.disputePageNumber + 1,
      this.rows,
      this.sortField,
      this.sortOrder,
      false,
    );
  }

  sortChange(event: { field: string; order: string }) {
    this.sortField = event.field;
    this.sortOrder =
      String(event.order) === '1' ? TABLE_PARAM.SORT_ASC_ORDER : TABLE_PARAM.SORT_DEC_ORDER;
    this.getDisputeData(
      this.disputePageNumber,
      this.disputePageSize,
      this.sortField,
      this.sortOrder,
      false,
    );
  }

  getDisputeTypes() {
    this.disputeService.getDisputeType().subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.disputeTypes = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant('DISPUTE_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DISPUTE_TYPE'),
          );
        }
      },
    });
  }

  getDisputeStatuses() {
    this.disputeService.getDisputeStatus().subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.disputeStatuses = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'DISPUTE_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DISPUTE_STATUS',
            ),
          );
        }
      },
    });
  }

  getDisputeData(
    pageNumber: number,
    pageSize: number,
    sortField: string,
    sortOrder: string,
    isExport: boolean,
  ) {
    const { userName, disputeTypes, disputeStatus } = this.filters;
    const params: DisputeFilterParams = {
      pageNumber,
      pageSize,
      userName,
      disputeTypes,
      disputeStatus,
      sortField,
      sortOrder,
      isExport,
    };
    this.disputeService.getFilteredDisputes(params).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          if (isExport === true) {
            this.filePath = this.baseApiUrl + response.filePath;

            if (this.filePath) {
              const fileName = this.filePath.split('/').pop() || '';
              CommonService.downloadFile(this.filePath, fileName);
            } else {
              this.toastService.showErrorToast(
                this.translateService.instant(
                  'DISPUTE_PAGE.TOAST_MESSAGE.FAILED_TO_EXPORT_DISPUTE_DATA',
                ),
              );
            }
          } else {
            this.disputeItems = response.data.items;
            this.disputeData = response.data;
            this.totalRecords = this.disputeData.totalCount;
          }
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant('DISPUTE_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DISPUTE_DATA'),
          );
        }
      },
    });
  }

  goToCallDetailsPage(callId: string, disputeId: string): void {
    let tree;

    if (!callId || callId === 'null') {
      tree = this.router.createUrlTree([`/dispute/${disputeId}`]);
    } else {
      tree = this.router.createUrlTree([`/call-details/${callId}/${disputeId}`]);
    }

    const url = this.router.serializeUrl(tree);
    const fullUrl = window.location.origin + this.location.prepareExternalUrl(url);

    window.open(fullUrl, '_blank');
  }
}
